import { Download, EyeOff } from "lucide-react";
import { Modal } from "../Modal";
import { Button } from "../ui/button";
import { FileGlyph } from "./FileGlyph";
import {
  downloadFile,
  fileDataUrl,
  fileTypeMeta,
  formatBytes,
  isImage,
  isPdf,
  isPreviewable,
  type PreviewableFile,
} from "./fileTypes";

export interface FilePreviewModalProps {
  open: boolean;
  file: PreviewableFile | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Diagonal watermark drawn over the document. Set to `null` to omit it —
   * only meaningful for previewable types, since a download prompt has
   * nothing to watermark.
   */
  watermark?: string | null;
}

/**
 * Inline preview for a single file before download. Images render in an
 * `<img>`, PDFs in an `<iframe>` (whose native viewer supplies page
 * navigation and zoom); everything else falls back to a download prompt
 * rather than an empty frame.
 */
export function FilePreviewModal({
  open,
  file,
  onOpenChange,
  watermark = "Preview Only",
}: FilePreviewModalProps) {
  const src = file ? fileDataUrl(file) : "";
  const meta = file ? fileTypeMeta(file.fileName, file.fileType) : null;
  const showWatermark = Boolean(watermark) && Boolean(file && isPreviewable(file.fileType));

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={file ? file.fileName : "Preview"}
      variant="extraWide"
      footer={
        file ? (
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              <EyeOff className="size-4" aria-hidden />
              Close Preview
            </Button>
            <Button onClick={() => downloadFile(file)}>
              <Download className="size-4" aria-hidden />
              Download
            </Button>
          </>
        ) : undefined
      }
    >
      {file ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <FileGlyph fileName={file.fileName} fileType={file.fileType} size="sm" />
            <div className="flex min-w-0 flex-col">
              <span
                className="truncate text-sm font-semibold text-foreground"
                title={file.fileName}
              >
                {file.fileName}
              </span>
              <span className="text-xs text-muted-foreground">
                {meta?.label} &nbsp;•&nbsp; {formatBytes(file.fileSize)}
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-muted">
            {isImage(file.fileType) ? (
              <div className="flex h-[70vh] items-center justify-center overflow-auto p-4">
                <img
                  src={src}
                  alt={file.fileName}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : isPdf(file.fileType) ? (
              <iframe
                src={src}
                title={file.fileName}
                className="h-[70vh] w-full border-0"
              />
            ) : (
              <div className="flex h-[40vh] flex-col items-center justify-center gap-3 text-center">
                <FileGlyph fileName={file.fileName} fileType={file.fileType} />
                <p className="text-sm text-muted-foreground">
                  Preview is not available for this file type.
                  <br />
                  Use Download to view it.
                </p>
              </div>
            )}

            {/* Non-interactive so the underlying viewer stays fully usable. */}
            {showWatermark ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                <span className="-rotate-[30deg] select-none whitespace-nowrap text-6xl font-bold uppercase tracking-widest text-muted-foreground/15">
                  {watermark}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No file selected.</p>
      )}
    </Modal>
  );
}
